<script setup>
import { computed, ref, watch, watchEffect } from "vue";
import { useMainStore }     from "@/store/main";
import { useApi } from "@/composables/useApi";
import {
	CompleteService,
	Notification,
	PhotoGallery,
	PhotoInput,
	Dropdown,
	Switcher,
	Input,
} from "@/components/ui/Index"
import {
	Close,
	Cat,
	Dog
} from '@/components/Icons';

const {
	deletePet,
	getBreeds,
	getMasters,
	getServices,
} = useApi();

const mainStore = useMainStore();

const formData  = defineModel();

const props = defineProps({
	cardId: Number,
});

const breedSuggest = ref({
	title      : 'Выберите породу',
	emptyValue : 'Нет нужной породы',
	values     : [],
})

const dropdownArray = ref([
	{
		id          : 0,
		title       : 'Выберите услугу',
		type        : 'Service',
		values      : [],
	},
	{
		id          : 1,
		title       : 'Выберите мастера',
		type        : 'Master',
		values      : [],
	}
]);

const dropdownDate = ref({
	id          : 0,
	placeholder : 'Введите дату и время',
	title       : 'Выбор даты и времени',
	type        : 'Date',
})

const completeSteps = ref([]);
const isUniqBreed   = ref(false);
const isComplete    = ref(false);
const uniqBreed     = ref(null);
const breed         = ref('');
const type          = ref('dog');

const notification = `Похоже, вашей породы пока нет в списке 😔 Но ничего страшного — прикрепите фото питомца, и мы свяжемся с Вами, чтобы подобрать мастера и услугу лично 💛`;

const completeDrafts = computed(() => {
	return formData.value.drafts.filter(el => el.status == 'draft')
})

const progressPercentage = computed(() => {
	return (completeSteps.value.length / maxStep.value) * 100;
})

const maxStep = computed(() => isUniqBreed.value ? 3 : 4)

const selectSuggestion = async suggestion => {
	if (suggestion.title === breedSuggest.value.emptyValue) {
		formData.value.drafts = [];

		isUniqBreed.value = true;
		formData.value.drafts.push({
			name: formData.value.name,
			status: 'need_feedback',
		})
	} else
		dropdownArray.value[0].values = await getServices(suggestion.id);

	const idx = completeSteps.value.indexOf('breed');

	if (idx == -1)
		completeSteps.value.push('breed');
}

const updateService = async ({ value }) => {
	dropdownArray.value[1].values = await getMasters(value);
}

const removeBreed = async () => {
	if (formData.value.pet_id)
		await deletePet(formData.value);

	formData.value.drafts = [];

	isUniqBreed.value = false;
	uniqBreed.value   = null;

	const idx = completeSteps.value.indexOf('breed');
	if (idx != -1)
		completeSteps.value.splice(idx, 1);
}

const handleInput = event => {
	const inputValue = event.target?.value;
	const hasBreed   = breedSuggest.value.values?.some(el => el.title == inputValue);
	const idx = completeSteps.value.indexOf('breed');
	
	if (idx != -1 && !hasBreed)
		completeSteps.value.splice(idx, 1);
	else if (idx == -1 && hasBreed)
		completeSteps.value.push('breed');
}

const handleUniqInput = ({ target }) => {
	const idx    = completeSteps.value.indexOf('uniqBreed');

	if (target) {
		if (target.value && idx == -1)
			completeSteps.value.push('uniqBreed');
		else if (!target.value && idx != -1)
			completeSteps.value.splice(idx, 1);
	}
}

const setComplete = ({ service, master }) => {
	const currentDraft = formData.value.drafts.find(el => el.master?.id == master.id);
	isComplete.value   = true;

	if (!currentDraft)
		formData.value.drafts.push({
			status   : 'draft',
			name     : formData.value.name,
			services : [ service ],
			master   : master,
		});
	else
		currentDraft.services.push(service);

	if (completeSteps.value.indexOf('draft') == -1)
		completeSteps.value.push('draft');
}

const removeDraft = () => {
	const idx = completeSteps.value.indexOf('draft');

	if (!formData.value.drafts.length) {
		isComplete.value = false;
		if (idx != -1)
			completeSteps.value.splice(idx, 1);
	}
}

const removeForm = async () => {
	const formIdx = mainStore.formData.indexOf(formData.value);
	
	if (mainStore.formData.length < 2)
		mainStore.isClosePopup = true;
	else {
		if (formData.value.pet_id)
			await deletePet(formData.value);
		mainStore.formData.splice(formIdx, 1);
	}
}

const updatePhotos = length => {
	const idx = completeSteps.value.indexOf('photos');

	if (length && idx == -1)
		completeSteps.value.push('photos');
	else if (!length && idx != -1)
		completeSteps.value.splice(idx, 1);
}

const setTime = () => {
	const idx = completeSteps.value.indexOf('time');

	if (idx == -1)
		completeSteps.value.push('time')
}

watch(() => type.value, async () => {
	if (formData.value.pet_id)
		await deletePet(formData.value);

	completeSteps.value = [];
	breed.value    = null;
	
	formData.value.drafts = [];
	formData.value.photos = [];
	delete formData.value.pet_id;
})

watchEffect(async () => breedSuggest.value.values = await getBreeds(type.value));

watchEffect(() => {
	if (formData.value.drafts[0] && uniqBreed.value) {
		formData.value.drafts[0].name = formData.value.name;
		formData.value.drafts[0].comment = `Указана порода которой нет: ${uniqBreed.value}`;
	}
})
</script>

<template>
	<div class="form pet__form">
		<button
			class="pet__delete-btn"
			@click="removeForm"
		>
			<Close />
		</button>
		<div class="pet__type">
			<Switcher
				v-model="type"
				:values="['dog', 'cat']"
			>
				<template #btn-1>
					<Dog />
					<span>Собака</span>
				</template>
				<template #btn-2>
					<Cat />
					<span>Кошка</span>
				</template>
			</Switcher>
		</div>
		<div class="progress-bar">
			<div class="progress-tube" :style="{ maxWidth: progressPercentage + '%' }" />
		</div>
		<div class="pet__options">
			<Input
				class="pet__name"
				name="pet__name"
				placeholder="Имя питомца"
				v-model:value="formData.name"
				:cyrillic-only="true"
			/>
			<Input
				class="pet__breed"
				name="pet__breed"
				placeholder="Введите название породы"
				v-model:value="breed"
				:suggestions="breedSuggest"
				:disabled="formData.drafts.length"
				@selectSuggestion="selectSuggestion"
				@clearInput="removeBreed"
				@input="handleInput"
				:cyrillic-only="true"
			/>
			<div class="pet__uniq-section" v-if="isUniqBreed && completeSteps.length">
				<Input
					class="pet__custom-breed"
					name="pet__custom-breed"
					placeholder="Введите название породы"
					v-model:value="uniqBreed"
					@input="handleUniqInput"
				/>
				<Notification :title="notification"/>
			</div>
			<div
				class="pet__complete-section"
				v-if="formData.drafts.some(el => el.status == 'draft')"
			>
				<CompleteService
					v-for="(_, index) in completeDrafts"
					v-model:draft="formData.drafts[index]"
					v-model:formData="formData"
					:dropdownDate
					@removeDraft="removeDraft"
					@setTime="setTime"
				/>
			</div>
			<div class="pet__default-section" v-if="!isUniqBreed && completeSteps.length">
				<Dropdown
					class="pet-service-draft"
					v-if="!isComplete"
					:dropdowns="dropdownArray"
					:cardId
					@setComplete="setComplete"
					@update:service="updateService"
				/>
				<button class="add-draft-btn" v-else @click="isComplete = false">
					<span>+</span>Добавить услугу
				</button>
			</div>
			<PhotoGallery
				v-model="formData.photos"
				v-if="formData.photos.length"
			/>
			<PhotoInput
				v-model="formData.photos"
				:petId="formData.pet_id"
				@updatePhotos="updatePhotos"
			/>
		</div>
	</div>
</template>

<style lang='scss'>
.pet__form {
	position: relative;
	border-radius: 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;

	&:first-child::before {
		width: 188px;
		height: 131px;
		position: absolute;
		top: -112px;
		content: '';
		background-image: url('public/cat.png');
	}

	@include mq($tablet) {
		&:first-child::before { display: none }
	}

	@include mq($tablet) { padding: 24px }
}

.pet__delete-btn {
	position: absolute;
	z-index: 3;
	right: 16px;
}

.pet__type {
	display: flex;
	gap: 20px;

	.switcher-wr {
		background-color: #FFF;

		button {
			padding-inline: 18.5px;
			display: flex;
			align-items: center;
			gap: 10px;
			font-weight: 500;
			font-size: 14px;
			font-family: "Raleway";
			font-weight: 500;
		}
	}
	
	.switcher__item {
		color: #000;

		&--active span { color: #FFF }
	}
}

.progress-bar {
	height: 2px;
	background-color: $softYellow;
}

.progress-tube {
	background-color: $yellow;
	width: 100%;
	height: 100%;
}

.pet__options {
	max-width: 520px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.pet__complete-section {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.add-draft-btn {
	width: 100%;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	background-color: $yellow;
	border-radius: 12px;
	font-size: 18px;
	font-family: 'Raleway';
	font-weight: 600;
	color: #FFF;

	span { font-size: 30px }
}
</style>